import { useState } from "react";
import {
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Launch } from "@mui/icons-material";
import apiService from "../../../../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../../../../components/common/ShowToast";

interface RegisterBannerProps {
  funeralHomeName: string;
  funeralHomeColor: string;
  websiteUrl?: string | null;
  isAdmin: boolean;
  streamId: number;
  enabled: boolean;
  onToggled?: () => void;
}

const RegisterBanner = ({
  funeralHomeName,
  funeralHomeColor,
  websiteUrl,
  isAdmin,
  streamId,
  enabled,
  onToggled,
}: RegisterBannerProps) => {
  const [saving, setSaving] = useState(false);

  const handleToggle = async (checked: boolean) => {
    setSaving(true);
    try {
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        funeral_home_banner_enabled: checked,
      });
      showSuccessToast(checked ? "Banner enabled" : "Banner disabled");
      onToggled?.();
    } catch {
      showErrorToast(
        "Failed to update banner visibility. Please try again.",
        "banner-toggle-error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!enabled && !isAdmin) return null;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      rowGap={1}
      sx={{
        mt: 3,
        p: 2,
        borderRadius: "10px",
        backgroundColor: funeralHomeColor,
        opacity: enabled ? 1 : 0.55,
      }}
    >
      <div>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
          Register for {funeralHomeName} Funeral Services
        </Typography>
        <Typography
          sx={{ color: "#e8f5e9", fontStyle: "italic", fontSize: "12px" }}
        >
          {enabled
            ? "Peace of mind today, comfort for tomorrow"
            : "Hidden from visitors — visible to admins only"}
        </Typography>
      </div>

      <Stack direction="row" spacing={1.5} alignItems="center">
        {websiteUrl && (
          <Button
            variant="outlined"
            size="small"
            endIcon={<Launch fontSize="small" />}
            component="a"
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "#fff",
              borderColor: "rgba(255,255,255,0.7)",
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(255,255,255,0.15)",
              },
            }}
          >
            Learn More
          </Button>
        )}

        {isAdmin && (
          <FormControlLabel
            sx={{ m: 0, color: "#fff" }}
            labelPlacement="start"
            label={
              <Typography sx={{ fontSize: "12px", color: "#fff" }}>
                Banner
              </Typography>
            }
            control={
              <Switch
                size="small"
                checked={enabled}
                disabled={saving}
                onChange={(e) => handleToggle(e.target.checked)}
              />
            }
          />
        )}
      </Stack>
    </Stack>
  );
};

export default RegisterBanner;
